import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EjercicioContable, PeriodoContable } from '../types/ejercicio';
import { EjercicioService } from '../services/ejercicioService';

interface EjercicioState {
  ejercicios: EjercicioContable[];
  periodos: PeriodoContable[];
  selectedEjercicio: EjercicioContable | null;
  loading: boolean;
  error: string | null;

  fetchEjercicios: () => Promise<void>;
  createEjercicio: (anio: number) => Promise<void>;
  cerrarEjercicio: (id: number) => Promise<void>;
  fetchPeriodos: (idEjercicio: number) => Promise<void>;
  cerrarPeriodo: (idPeriodo: number) => Promise<void>;
  abrirPeriodo: (idPeriodo: number) => Promise<void>;
  setSelectedEjercicio: (ejercicio: EjercicioContable | null) => void;
  clearError: () => void;
}

export const useEjercicioStore = create<EjercicioState>()(
  devtools(
    (set, get) => ({
      ejercicios: [],
      periodos: [],
      selectedEjercicio: null,
      loading: false,
      error: null,

      fetchEjercicios: async () => {
        set({ loading: true, error: null }, false, 'ejercicio/fetch/start');
        try {
          const ejercicios = await EjercicioService.getAll();
          set({ ejercicios, loading: false }, false, 'ejercicio/fetch/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar ejercicios';
          set({ loading: false, error: errorMessage }, false, 'ejercicio/fetch/error');
        }
      },

      createEjercicio: async (anio) => {
        set({ loading: true, error: null }, false, 'ejercicio/create/start');
        try {
          await EjercicioService.create(anio);
          const ejercicios = await EjercicioService.getAll();
          set({ ejercicios, loading: false }, false, 'ejercicio/create/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al crear ejercicio';
          set({ loading: false, error: errorMessage }, false, 'ejercicio/create/error');
          throw error;
        }
      },

      cerrarEjercicio: async (id) => {
        set({ loading: true, error: null }, false, 'ejercicio/cerrar/start');
        try {
          await EjercicioService.cerrar(id);
          const ejercicios = await EjercicioService.getAll();
          set((state) => ({
            ejercicios,
            loading: false,
            selectedEjercicio: state.selectedEjercicio?.idEjercicio === id
              ? ejercicios.find(e => e.idEjercicio === id) || null
              : state.selectedEjercicio,
          }), false, 'ejercicio/cerrar/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cerrar ejercicio';
          set({ loading: false, error: errorMessage }, false, 'ejercicio/cerrar/error');
          throw error;
        }
      },

      fetchPeriodos: async (idEjercicio) => {
        set({ loading: true, error: null }, false, 'ejercicio/fetchPeriodos/start');
        try {
          const periodos = await EjercicioService.getPeriodos(idEjercicio);
          set({ periodos, loading: false }, false, 'ejercicio/fetchPeriodos/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar periodos';
          set({ loading: false, error: errorMessage }, false, 'ejercicio/fetchPeriodos/error');
        }
      },

      cerrarPeriodo: async (idPeriodo) => {
        set({ loading: true, error: null }, false, 'ejercicio/cerrarPeriodo/start');
        try {
          await EjercicioService.cerrarPeriodo(idPeriodo);
          const { selectedEjercicio } = get();
          if (selectedEjercicio) {
            const periodos = await EjercicioService.getPeriodos(selectedEjercicio.idEjercicio);
            set({ periodos, loading: false }, false, 'ejercicio/cerrarPeriodo/success');
          } else {
            set({ loading: false }, false, 'ejercicio/cerrarPeriodo/success');
          }
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cerrar periodo';
          set({ loading: false, error: errorMessage }, false, 'ejercicio/cerrarPeriodo/error');
          throw error;
        }
      },

      abrirPeriodo: async (idPeriodo) => {
        set({ loading: true, error: null }, false, 'ejercicio/abrirPeriodo/start');
        try {
          await EjercicioService.abrirPeriodo(idPeriodo);
          const { selectedEjercicio } = get();
          if (selectedEjercicio) {
            const periodos = await EjercicioService.getPeriodos(selectedEjercicio.idEjercicio);
            set({ periodos, loading: false }, false, 'ejercicio/abrirPeriodo/success');
          } else {
            set({ loading: false }, false, 'ejercicio/abrirPeriodo/success');
          }
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al abrir periodo';
          set({ loading: false, error: errorMessage }, false, 'ejercicio/abrirPeriodo/error');
          throw error;
        }
      },

      setSelectedEjercicio: (ejercicio) => set({ selectedEjercicio: ejercicio, periodos: [] }, false, 'ejercicio/setSelected'),
      clearError: () => set({ error: null }, false, 'ejercicio/clearError'),
    }),
    { name: 'EjercicioStore' }
  )
);
